import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { cloneDeep, isEqual } from 'lodash';

// TODO: Fix old states dependency in enclosed module
import {
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import { SemboDate } from '@libs/sembo-dates';
import { assertValue, isDefined } from '@libs/utils';
import type { TicketRate } from '@libs/trip-planner-interfaces';

import {
  getActivityTotalPrice,
  getSelectedRatesByOffset,
  getIngredientStayDuration,
  getRatesToDisplay,
  getSelectedTicketAvailabilty,
  hasActivityAvailabilityInSuggestedDates,
} from './activityDetailsTicketsStoreLogic';
import { useActivityDetailsStore } from './activityDetailsStore';

export const useActivityDetailsTicketsStore = defineStore(
  'ActivityDetailsTicketsStore',
  () => {
    const activityDetailsStore = useActivityDetailsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const { preferredDay } = storeToRefs(preferredTimeStore);
    const tripStore = useTripStore();
    const routerStore = useRouterStore();
    const statsigEventsStore = useStatsigEventsStore();
    const recipeStore = useRecipeStore();

    // State
    const selectedDate = ref<SemboDate | undefined>();
    const selectedRatesValidityStatus = ref<Record<string, boolean>>({});
    const initialSelectedTickets = cloneDeep(
      recipeStore.selectedActivityTickets
    );

    // Getters
    const activityDetails = computed(
      () => activityDetailsStore.activityDetails
    );

    const bookableDays = computed(() => {
      if (!activityDetails.value) {
        return undefined;
      }

      return getIngredientStayDuration(
        activityDetails.value.ingredientDate,
        activityDetails.value.numberOfNights
      );
    });

    const startDate = computed(() => {
      if (!bookableDays.value) {
        return undefined;
      }

      return bookableDays.value[0];
    });

    const selectedDateOffset = computed<number | undefined>(() => {
      if (!selectedDate.value || !startDate.value) {
        return undefined;
      }
      return selectedDate.value.diff(startDate.value, 'day');
    });

    // Actions

    function setInitialSelectedDate(): void {
      if (!startDate.value) {
        return;
      }

      selectedDate.value = new SemboDate(startDate.value.toISOString()).add({
        days: getSubingredientOffset(),
      });
    }

    function getSubingredientOffset(): number {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > getSubingredientOffset > activityDetails'
      );
      const subIngredient = recipeStore.getSubIngredientsByCode(
        activityDetails.value.parentIngredientId,
        activityDetails.value.productCode
      )?.[0];

      return subIngredient?.offset ?? 0;
    }

    function isSelectedDate(date: Date): boolean {
      if (!selectedDate.value) {
        return false;
      }
      return selectedDate.value.isSame(date);
    }

    function selectDate(date: Date): void {
      selectedDate.value = new SemboDate(date);
    }

    function thereIsRatesAvailabilityInCurrentMonth(): boolean {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > thereIsRatesAvailabilityInCurrentMonth > activityDetails'
      );
      return activityDetails.value.availability.some(
        (ticket) => !!ticket.rates.length
      );
    }

    function thereIsTicketAvailability(offset: number): boolean {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > thereIsTicketAvailability > activityDetails'
      );

      const activityHasAvailabilityInSuggestedDates =
        hasActivityAvailabilityInSuggestedDates(activityDetails.value);

      return preferredDay.value || activityHasAvailabilityInSuggestedDates
        ? !!getSelectedRatesByOffset(
            offset,
            activityDetails.value.availability,
            activityDetails.value.ingredientDate
          ).length
        : thereIsRatesAvailabilityInCurrentMonth();
    }

    function updateTicketRateValidityStatus(
      ticketRateValidityStatus: boolean,
      index: number
    ): void {
      const dayIndex = selectedDateOffset.value;
      const rateByDayIndex = `${dayIndex}${index}`;
      selectedRatesValidityStatus.value[rateByDayIndex] =
        ticketRateValidityStatus;
      tripStore.updateAreAllSelectedRatesValid(
        Object.values(selectedRatesValidityStatus.value).every(isDefined)
      );
    }

    async function selectTicket(): Promise<void> {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > selectTicket > activityDetails'
      );
      assertValue(
        recipeStore.selectedActivityTickets,
        'activityDetailsTicketsStore.ts > selectTicket > selectedActivityTickets'
      );

      const availableSelectedActivityTickets =
        recipeStore.selectedActivityTickets.filter((selectedTicket) => {
          assertValue(
            activityDetails.value,
            'activityDetailsTicketsStore.ts > selectTicket > activityDetails'
          );
          return !!getSelectedTicketAvailabilty(
            activityDetails.value.availability,
            selectedTicket,
            preferredDay.value,
            activityDetails.value.ingredientDate
          );
        });
      statsigEventsStore.logActivityDetailsTicketSelected({
        productCode: activityDetails.value.productCode,
      });
      tripStore.updateActivitySubIngredients(
        activityDetails.value.productCode,
        availableSelectedActivityTickets,
        activityDetails.value.parentIngredientId
      );

      // NOTE: We need to wait for the route to be updated with selected activity before redirecting to overview
      await nextTick();
      await routerStore.navigateToTripOverview();
    }

    // Getters
    const selectedActivityTotalPrice = computed(() => {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > selectedActivityTotalPrice > activityDetails'
      );
      return getActivityTotalPrice(
        recipeStore.selectedActivityTickets,
        activityDetails.value.availability,
        preferredDay.value,
        activityDetails.value.ingredientDate
      );
    });

    const isActivitySelected = computed<boolean>(() => {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > isActivitySelected > activityDetails'
      );

      const subIngredients = recipeStore.getSubIngredientsByCode(
        activityDetails.value.parentIngredientId,
        activityDetails.value.productCode
      );

      return !!subIngredients && subIngredients.length > 0;
    });

    const ticketRatesInSelectedDate = computed<TicketRate[]>(() => {
      assertValue(
        activityDetails.value,
        'activityDetailsTicketsStore.ts > isActivitySelected > activityDetails'
      );
      assertValue(
        selectedDateOffset.value,
        'activityDetailsTicketsStore.ts > isActivitySelected > activityDetails'
      );
      return preferredDay.value
        ? getSelectedRatesByOffset(
            selectedDateOffset.value,
            activityDetails.value.availability,
            activityDetails.value.ingredientDate
          )
        : getRatesToDisplay(activityDetails.value.availability);
    });

    const selectedActivityHasChanged = computed(
      () =>
        !isEqual(initialSelectedTickets, recipeStore.selectedActivityTickets)
    );
    const shouldScrollToTicket = computed<boolean>(
      () =>
        !isActivitySelected.value &&
        (!selectedActivityHasChanged.value ||
          !tripStore.areAllSelectedRatesValid)
    );

    return {
      // State
      bookableDays,

      // Actions
      isSelectedDate,
      thereIsTicketAvailability,
      selectDate,
      updateTicketRateValidityStatus,
      selectTicket,
      setInitialSelectedDate,

      // Getters
      selectedActivityTotalPrice,
      isActivitySelected,
      ticketRatesInSelectedDate,
      selectedDateOffset,
      shouldScrollToTicket,
    };
  }
);
